/** Классификатор обрыва соединения, счётчик поколений и доведение
 *  переоткрытия до конца — без зависимости от `./db`.
 *
 *  Вынесено отдельным модулем ровно затем, чтобы разорвать цикл импортов:
 *  центральный слой Dexie (DBCore middleware, `db.ts`) регистрируется
 *  ВНУТРИ конструктора `TrainerDb`, до первого открытия базы, и ему нужны
 *  ровно эти вещи — классификатор, счётчик поколений и `довестиОткрытие`.
 *  Если бы они брали `db` (значение) из `dbReconnect.ts`, а `dbReconnect.ts`
 *  при этом импортировал `db` (он импортирует — `db.close()` в
 *  `переоткрытьСоединение`), `db.ts` и `dbReconnect.ts` образовали бы цикл:
 *  модуль, который ещё не кончил исполняться сам, использовал бы то, что
 *  должен экспортировать.
 *
 *  Решение — параметризация, а не импорт: `довестиОткрытие(dexieDb, …)`
 *  принимает базу ДОВОДОМ (тип `Dexie`, ЧУЖОЙ модуль, а не наш `./db`), а не
 *  берёт её из замыкания над `export const db`. И `db.ts` (у которого
 *  `dexieDb` уже есть параметром `installConnectionGuard`), и
 *  `dbReconnect.ts` (у которого есть `db`, импортированный как обычно, БЕЗ
 *  цикла — `dbReconnect.ts → db.ts`, однонаправленно) зовут одну и ту же
 *  функцию, передавая ей свою ссылку на базу. Цикл `db.ts → dbReconnect.ts →
 *  db.ts` так и не возникает: `db.ts` эту функцию берёт ОТСЮДА, из модуля,
 *  который `./db` не знает вовсе.
 *
 *  `../sentry` — тоже без цикла: `sentry.ts` ничего не импортирует из
 *  `src/data/`. */
import type Dexie from 'dexie';
import { report } from '../sentry';

/** Имена отказов, которыми Dexie 4.4.6 и сам браузер сообщают об оборванном
 *  соединении к IndexedDB — а не о нарушенном предусловии или отказе
 *  проверки данных.
 *
 *  `UnknownError` — «Unable to open cursor», ровно то, что шлёт WebKit,
 *  когда Safari рвёт соединение с базой после ухода вкладки в фон и не
 *  чинит его сама. `InvalidStateError` — «database connection is closing»,
 *  тот же обрыв с другой стороны. `DatabaseClosedError` — имя, которое
 *  Dexie даёт СВОЕМУ отказу после явного `db.close()`. `OpenFailedError` —
 *  своя ошибка Dexie на транзакции, созданной, но не начатой в момент
 *  чужого `db.close()` (подробности — `dbReconnect.ts`, откуда список
 *  переехал без изменений). */
const ОБРЫВ_СОЕДИНЕНИЯ = new Set([
  'UnknownError', 'InvalidStateError', 'DatabaseClosedError', 'OpenFailedError',
]);

/** Имена ПОСТОЯННЫХ причин неудачного открытия (Dexie 4.4.6, `dexieOpen`
 *  и `Transaction.create` в `dexie.mjs`). Перезагрузка их не лечит: то же
 *  самое откроется так же, поэтому такая причина — НЕ обрыв, даже когда её
 *  несёт `OpenFailedError` или `DatabaseClosedError` (обёртка Dexie с
 *  `.inner` — настоящей причиной открытия).
 *
 *  - `VersionError` — база на диске новее версии схемы кода (откат выкладки).
 *  - `UpgradeError` — Dexie отказалась менять схему (например, первичный ключ).
 *  - `SchemaError` — схема в коде сама недопустима; от перезапуска не изменится.
 *  - `MissingAPIError` — в браузере нет IndexedDB вовсе.
 *  - `SecurityError` — политика браузера запрещает сайту IndexedDB. */
const ПОСТОЯННЫЙ_ОТКАЗ_ОТКРЫТИЯ = new Set([
  'VersionError', 'UpgradeError', 'SchemaError', 'MissingAPIError', 'SecurityError',
]);

/** Предел глубины разбора цепочки `.inner`/`BulkError.failures`. Защита от
 *  зацикливания на кольце ссылок, а не ожидаемый рабочий случай: настоящая
 *  цепочка Dexie — одна-две ошибки в глубину. */
const МАКС_ГЛУБИНА_ЦЕПОЧКИ = 8;

/** Признак обрыва соединения — по ВСЕЙ цепочке ошибки, а не только по её
 *  собственному имени.
 *
 *  Три источника, почему собственного имени мало:
 *
 *  1. Dexie заворачивает нативный `DOMException` в свою ошибку с тем же
 *     именем, но `.inner` — ссылка на настоящую причину (`mapError` в
 *     `dexie.mjs`). Для наших четырёх имён обёртка не меняет `.name`, но
 *     `AbortError`, которым легитимно кончается ЛЮБАЯ прерванная
 *     транзакция, — обёртка ЧУЖОГО отказа: `AbortError` сам по себе НЕ
 *     признак обрыва (иначе любая законно прерванная транзакция считалась
 *     бы обрывом связи), а `AbortError`, чей `.inner` — `UnknownError` от
 *     оборванного соединения, — признак.
 *  2. `BulkError` (`bulkPut`/`bulkAdd`/`bulkDelete`/`bulkUpdate`) заворачивает
 *     НЕСКОЛЬКО отказов сразу, в `.failures` (массив). Пачка, где обрыв
 *     соединения погубил один запрос из тысячи, обязана распознаваться так
 *     же, как одиночный отказ тем же именем.
 *  3. Пакетная операция письма (`mutate` в `dexie.mjs`, ~3349–3430) не
 *     бросает на асинхронный отказ ОДНОГО запроса вовсе — она гасит его
 *     (`preventDefault`) и резолвится успехом с `{ numFailures, failures }`;
 *     `BulkError` бросает уже слой ВЫШЕ (`Table.bulkPut()` и соседи,
 *     ~1806). Это не отказ этой функции — вызывающий (центральный слой в
 *     `db.ts`) обязан заглянуть в `failures` результата `mutate` САМ, эта
 *     функция классифицирует уже готовую ошибку `BulkError`, если её
 *     построили выше. */
export function отказСоединения(error: unknown, глубина = 0): boolean {
  if (!(error instanceof Error)) return false;
  if (постоянныйОтказОткрытия(error)) return false;
  if (ОБРЫВ_СОЕДИНЕНИЯ.has(error.name)) return true;
  if (глубина >= МАКС_ГЛУБИНА_ЦЕПОЧКИ) return false;

  const inner = (error as { inner?: unknown }).inner;
  if (inner != null && отказСоединения(inner, глубина + 1)) return true;

  if (error.name === 'BulkError') {
    const failures = (error as { failures?: unknown }).failures;
    if (Array.isArray(failures)) {
      return failures.some((failure) => отказСоединения(failure, глубина + 1));
    }
  }
  return false;
}

/** Есть ли в цепочке `.inner` ошибки ПОСТОЯННАЯ причина неудачного
 *  открытия (`ПОСТОЯННЫЙ_ОТКАЗ_ОТКРЫТИЯ`). Тот же предел глубины, что у
 *  `отказСоединения`. Измерено (Dexie 4.4.6 + fake-indexeddb): после
 *  неудачного открытия запрос отдаёт `DatabaseClosedError` с `.inner`
 *  `VersionError`, а само `open()` — сам `VersionError`; по имени обёртки
 *  постоянный отказ выглядел бы обрывом. */
export function постоянныйОтказОткрытия(error: unknown, глубина = 0): boolean {
  if (!(error instanceof Error) || глубина >= МАКС_ГЛУБИНА_ЦЕПОЧКИ) return false;
  if (ПОСТОЯННЫЙ_ОТКАЗ_ОТКРЫТИЯ.has(error.name)) return true;
  return постоянныйОтказОткрытия((error as { inner?: unknown }).inner, глубина + 1);
}

/** Поколение соединения: счётчик, растущий на единицу при КАЖДОМ настоящем
 *  `db.close()`, где бы он ни был позван — из центрального слоя в `db.ts`
 *  или из `переоткрытьСоединение` в `dbReconnect.ts`. Общий счётчик — это и
 *  есть то, что не даёт двум местам, поймавшим один и тот же обрыв, закрыть
 *  соединение дважды (подробный довод — в `dbReconnect.ts`, `М1`). */
let поколение = 0;

export function текущееПоколение(): number {
  return поколение;
}

/** Продвинуть поколение, если оно ещё совпадает со `снятое`, — то есть если
 *  вызывающий первым добрался до продвижения с тех пор, как снял его.
 *
 *  Возвращает `true`, если продвинула: тогда закрывать соединение обязан
 *  именно вызывающий, ему же передаётся право реально позвать `db.close()`.
 *  Возвращает `false`, если поколение уже сменилось, — кто-то другой уже
 *  переоткрыл то же самое соединение, и закрывать снова нельзя: это убило
 *  бы транзакции, вставшие в очередь уже на СВЕЖЕМ соединении.
 *
 *  `снятое === undefined` продвигает безусловно — тот же смысл, что и у
 *  прежнего `переоткрытьСоединение()` без довода. */
export function попытатьсяПродвинутьПоколение(снятое?: number): boolean {
  if (снятое !== undefined && снятое !== поколение) return false;
  поколение += 1;
  return true;
}

/** Имя причины для следа в Sentry — только `.name`, никогда текст.
 *
 *  Текст обрыва бывает произвольным (у WebKit — «Unable to open cursor»,
 *  у Dexie после переоткрытия ЧУЖим местом — своё), а `.name` — из
 *  закрытого списка `ОБРЫВ_СОЕДИНЕНИЯ` выше и одинаково для одного и того
 *  же механизма отказа что в проде, что в тесте. `причина`, не являющаяся
 *  `Error` (например, `undefined` у ручного вызова `переоткрытьСоединение()`
 *  без довода), даёт `'неизвестно'`, а не бросает. */
export function имяОтказа(причина: unknown): string {
  return причина instanceof Error ? причина.name : 'неизвестно';
}

/** Сколько раз пытаемся ОТКРЫТЬ соединение заново внутри `довестиОткрытие`.
 *
 *  Не путать с `МАКС_ПОПЫТОК` в `dbReconnect.ts` — та считает попытки
 *  ДЕЙСТВИЯ (`get`/`put`/…, безопасного к полному повтору), эта — попытки
 *  самого `dexieDb.open()`. */
const МАКС_ПОПЫТОК_ОТКРЫТИЯ = 3;

/** Предел ОДНОЙ попытки открытия по умолчанию. WebKit, по измерению ревью
 *  (клетка B пробного файла), иногда не отвечает на `indexedDB.open` вовсе
 *  — без предела зависшее открытие держит `loadSessions`, проверку при
 *  возврате и `loadDevice` немыми бесконечно, и Sentry не получает ничего:
 *  событие некому отправить, пока промис висит. */
const ТАЙМАУТ_ОТКРЫТИЯ_МС = 10_000;

/** Паузы МЕЖДУ неудачными (ОТКАЗАВШИМИ, не зависшими) попытками открытия.
 *  Длина — на одну меньше числа попыток: после последней паузы нет, она
 *  сразу ведёт к отчёту и перезагрузке. */
const ПАУЗЫ_МС: readonly number[] = [1000, 2000];

/** Не чаще одного раза в этот промежуток — иначе устройство, где
 *  IndexedDB не открывается вовсе (диск сломан, политика организации),
 *  перезагружало бы вкладку по кругу, не давая родителю даже прочитать
 *  экран между перезагрузками. */
const ИНТЕРВАЛ_ПЕРЕЗАГРУЗКИ_МС = 10 * 60 * 1000;

const КЛЮЧ_ПОСЛЕДНЕЙ_ПЕРЕЗАГРУЗКИ = 'mult-trainer:обрыв-соединения:последняя-перезагрузка';

export interface НастройкиДоведения {
  /** Предел одной попытки `dexieDb.open()`, мс. По умолчанию 10 000; тесты
   *  передают меньшее значение, чтобы не ждать по-настоящему. */
  таймаутМс?: number;
  /** Паузы между неудачными попытками, мс. По умолчанию `[1000, 2000]`. */
  паузыМс?: readonly number[];
  /** Перезагрузка страницы — внедряемая функция ради тестов (шпион) и ради
   *  того, что `connectionGuard.ts` не имеет права трогать `window`
   *  напрямую на уровне модуля (окружение теста — `node`, не `jsdom`). По
   *  умолчанию `() => window.location.reload()`. */
  перезагрузить?: () => void;
  /** Часы для отметки времени последней перезагрузки, ради тестов. По
   *  умолчанию `Date.now`. */
  now?: () => number;
}

/** Единственный промис доведения на весь модуль — общий для ВСЕХ
 *  одновременных вызовов (`переоткрытьСоединение` из `dbReconnect.ts` и
 *  сторож из `db.ts` могут захотеть довести открытие одновременно на один
 *  и тот же обрыв). Без этого два параллельных вызова запустили бы два
 *  независимых цикла из трёх попыток каждый — и, при неудаче, могли бы
 *  перезагрузить страницу дважды или удвоить число открытий, которые
 *  Dexie и так уже сама сериализует. */
let текущееДоведение: Promise<void> | null = null;

/** Отметка «первое успешное доведение на этой странице уже было» — держит
 *  единственность события `info` за жизнь страницы (раздел «Видимость
 *  восстановления» задания). Сбрасывается только перезагрузкой страницы —
 *  то есть ровно тогда, когда «эта страница» и кончается. */
let успешноеДоведениеБыло = false;

/** Только для тестов: без сброса второй тест в файле не увидел бы СВОЕГО
 *  события `info` — отметка уже стояла бы от первого. Экспортируется
 *  открыто, а не спрятана за `__`, по тому же духу, что `TrainerDb`
 *  принимает имя базы доводом «для теста, а не для тайны». */
export function сброситьДоведениеДляТестов(): void {
  успешноеДоведениеБыло = false;
  текущееДоведение = null;
}

/** Только для тестов: дождаться, чтобы фоновое доведение ПРЕДЫДУЩЕГО теста
 *  (запущенное через `отпустить` и потому ничем внешним не ожидаемое) не
 *  просочилось в следующий тест — своим `report` или сменой поколения в
 *  случайный момент. */
export function дождатьсяТекущегоДоведенияДляТестов(): Promise<void> {
  return текущееДоведение ?? Promise.resolve();
}

function пауза(мс: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, мс);
  });
}

type ИсходОткрытия =
  | { тип: 'успех' }
  | { тип: 'отказ'; ошибка: unknown }
  | { тип: 'зависло' };

/** Одна попытка `dexieDb.open()` наперегонки с пределом `таймаутМс`.
 *
 *  Не бросает: три исхода различаются ЗНАЧЕНИЕМ, а не тем, отклонился ли
 *  промис, — «зависло» не отклонение вовсе, а сторонний таймер, дошедший
 *  раньше самого открытия, и заворачивать это в try/catch было бы неточно.
 *  Оставшийся висеть настоящий `dexieDb.open()` после срабатывания таймера
 *  никуда не девается — Dexie 4.4.6 не умеет отменить уже идущее открытие
 *  (измерение ревью, клетка B), и это ровно то, ради чего вызывающий
 *  дальше не повторяет попытку открытия, а сразу сдаётся. */
function попыткаОткрытия(dexieDb: Dexie, таймаутМс: number): Promise<ИсходОткрытия> {
  return new Promise((resolve) => {
    let решено = false;
    const таймер = setTimeout(() => {
      if (решено) return;
      решено = true;
      resolve({ тип: 'зависло' });
    }, таймаутМс);
    dexieDb.open().then(
      () => {
        if (решено) return;
        решено = true;
        clearTimeout(таймер);
        resolve({ тип: 'успех' });
      },
      (ошибка: unknown) => {
        if (решено) return;
        решено = true;
        clearTimeout(таймер);
        resolve({ тип: 'отказ', ошибка });
      },
    );
  });
}

/** Хранилища для отметки о перезагрузке. Доступ к самому идентификатору
 *  тоже может бросить (запрет сайту — `SecurityError`; в тесте под `node` —
 *  `ReferenceError`), поэтому берутся функциями и каждое — в своём try. */
const ХРАНИЛИЩА: ReadonlyArray<() => Storage> = [
  () => sessionStorage,
  () => localStorage,
];

/** Перезагрузка страницы с защитой от петли: не чаще одного раза в
 *  `ИНТЕРВАЛ_ПЕРЕЗАГРУЗКИ_МС`. Отметка читается из ОБОИХ хранилищ
 *  (`sessionStorage`, `localStorage`), каждое в своём try/catch, —
 *  действует самая поздняя из прочитанных; пишется во все, что принимают
 *  запись. Если не приняло ни одно — отследить петлю нечем, и страница НЕ
 *  перезагружается, а сообщает (перезагрузка без предела хуже немого
 *  экрана ошибки). */
function перезагрузитьСЗащитойОтПетли(
  перезагрузить: () => void,
  now: () => number,
): void {
  const момент = now();
  let последняя: number | null = null;
  for (const взять of ХРАНИЛИЩА) {
    try {
      const сохранённая = взять().getItem(КЛЮЧ_ПОСЛЕДНЕЙ_ПЕРЕЗАГРУЗКИ);
      const число = сохранённая === null ? NaN : Number(сохранённая);
      if (!Number.isNaN(число) && (последняя === null || число > последняя)) последняя = число;
    } catch {
      // Это хранилище недоступно — остальные всё равно опрашиваются.
    }
  }
  if (последняя !== null && момент - последняя < ИНТЕРВАЛ_ПЕРЕЗАГРУЗКИ_МС) {
    report(
      new Error('перезагрузка после обрыва соединения подавлена: слишком рано после предыдущей'),
      'перезагрузка-подавлена',
    );
    return;
  }
  let записано = 0;
  for (const взять of ХРАНИЛИЩА) {
    try {
      взять().setItem(КЛЮЧ_ПОСЛЕДНЕЙ_ПЕРЕЗАГРУЗКИ, String(момент));
      записано += 1;
    } catch {
      // Это хранилище не приняло запись — пробуем остальные.
    }
  }
  if (записано === 0) {
    report(
      new Error('перезагрузка после обрыва соединения подавлена: хранилище недоступно, петлю не отследить'),
      'перезагрузка-подавлена',
    );
    return;
  }
  перезагрузить();
}

/** Довести переоткрытие соединения до конца.
 *
 *  `dexieDb.close({disableAutoOpen:false})` (уже позванный вызывающим —
 *  этой функции сам `close` не касается) снимает соединение, но САМ его
 *  не открывает: следующий запрос сделал бы это лениво, а если такого
 *  запроса не случится (экран простаивает, `проверитьСоединение` тоже
 *  ничего не позвала) или само открытие зависнет (клетка B), приложение
 *  остаётся немым до ручной перезагрузки.
 *
 *  До `МАКС_ПОПЫТОК_ОТКРЫТИЯ` попыток `dexieDb.open()`, каждая наперегонки
 *  с пределом. ОТКАЗ (не зависание) — закрыть ещё раз
 *  (`disableAutoOpen:false` возвращает автооткрытие для следующей попытки,
 *  как и после обычного обрыва) и подождать паузу перед следующей.
 *  ЗАВИСАНИЕ — НЕ повторяем: вторая попытка легла бы на то же самое
 *  зависшее открытие (Dexie его не отменяет), а не на новое. Быстрее и
 *  честнее сразу перезагрузить.
 *
 *  Один промис на все одновременные вызовы — `текущееДоведение` выше. */
export function довестиОткрытие(dexieDb: Dexie, настройки: НастройкиДоведения = {}): Promise<void> {
  if (текущееДоведение) return текущееДоведение;
  текущееДоведение = довестиОткрытиеОднажды(dexieDb, настройки).finally(() => {
    текущееДоведение = null;
  });
  return текущееДоведение;
}

async function довестиОткрытиеОднажды(
  dexieDb: Dexie,
  {
    таймаутМс = ТАЙМАУТ_ОТКРЫТИЯ_МС,
    паузыМс = ПАУЗЫ_МС,
    перезагрузить = () => window.location.reload(),
    now = () => Date.now(),
  }: НастройкиДоведения,
): Promise<void> {
  let последняяОшибка: unknown;
  for (let попытка = 1; попытка <= МАКС_ПОПЫТОК_ОТКРЫТИЯ; попытка += 1) {
    const исход = await попыткаОткрытия(dexieDb, таймаутМс);
    if (исход.тип === 'успех') {
      if (!успешноеДоведениеБыло) {
        успешноеДоведениеБыло = true;
        report(
          new Error('переоткрытие соединения с IndexedDB восстановлено'),
          'переоткрытие:успех',
          'info',
        );
      }
      return;
    }
    if (исход.тип === 'зависло') {
      report(new Error('открытие IndexedDB не отвечает дольше предела'), 'переоткрытие:зависло');
      перезагрузитьСЗащитойОтПетли(перезагрузить, now);
      return;
    }
    if (постоянныйОтказОткрытия(исход.ошибка)) {
      // Постоянный отказ (`VersionError` и родня) ни повтор, ни перезагрузка
      // не лечат — не крутим петлю: одно событие и выход, запросы получат
      // отказ как есть (экран ошибки лучше перезагрузок по кругу).
      report(исход.ошибка as Error, 'переоткрытие:постоянный-отказ');
      return;
    }
    последняяОшибка = исход.ошибка;
    dexieDb.close({ disableAutoOpen: false });
    if (попытка < МАКС_ПОПЫТОК_ОТКРЫТИЯ) {
      await пауза(паузыМс[попытка - 1] ?? паузыМс[паузыМс.length - 1] ?? 1000);
    }
  }
  report(
    последняяОшибка instanceof Error
      ? последняяОшибка
      : new Error('не удалось переоткрыть соединение с IndexedDB'),
    'переоткрытие:не-открылась',
  );
  перезагрузитьСЗащитойОтПетли(перезагрузить, now);
}
